import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

/**
 * Search alerts — gracefully empty when backend has no alert records.
 * CTA sends users to browse so they can refine filters (alert creation UX).
 */
const SearchAlerts = ({ searchAlerts = [], isLoading }) => {
  const navigate = useNavigate();
  const list = Array.isArray(searchAlerts) ? searchAlerts : [];

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Search alerts</h2>
          <p className="text-xs text-slate-500 mt-0.5">Get notified when matches appear</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconName="Search"
          iconPosition="left"
          onClick={() => navigate('/property-search-browse')}
        >
          Browse
        </Button>
      </div>

      {isLoading ? (
        <div className="p-5 space-y-3">
          <div className="h-16 rounded-lg bg-slate-100 animate-pulse" />
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-10 px-5">
          <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="Bell" size={20} className="text-orange-500" />
          </div>
          <p className="text-sm font-medium text-slate-900 mb-1">No alerts set up</p>
          <p className="text-xs text-slate-500 mb-4 max-w-xs mx-auto">
            Browse the market and save favourites, or post a Specific Property Request so agents
            can match you proactively.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/property-search-browse')}
              iconName="Search"
              iconPosition="left"
            >
              Find properties
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/notice-board')}
              iconName="ClipboardList"
              iconPosition="left"
            >
              Post request
            </Button>
          </div>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {list.map((alert) => (
            <li key={alert.id} className="px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name="MapPin" size={14} className="text-slate-400" />
                    <span className="text-sm font-semibold text-slate-900 truncate">
                      {alert.location || alert.city || alert.title || 'Saved search'}
                    </span>
                    <span
                      className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${
                        alert.isActive || alert.is_active || alert.status === 'active'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {alert.isActive || alert.is_active || alert.status === 'active'
                        ? 'Active'
                        : 'Paused'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">
                    {[alert.propertyType || alert.property_type, alert.priceRange || alert.price_range]
                      .filter(Boolean)
                      .join(' · ') || 'Custom criteria'}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/property-search-browse')}
                >
                  View
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default SearchAlerts;
